"use client";

import { Sparkles } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@/i18n/routing";
import { cn } from "@/utilities/cn";
import styles from "./liquid-metal-button.module.css";

/** Face/label tone. `auto` follows the theme: white in light, black in dark. */
type MetalAppearance = "dark" | "light" | "auto";

/**
 * Rim/glow tints — the metal face stays a dark slab (see PHASE-05-BUTTONS.md:
 * "2px rim over #202020 → #000, do not widen"). A variant only colours the
 * halo/rim, keeping the white label legible. Glow values lifted from the
 * `data-pal` palettes in design/metal-lab.html.
 */
type MetalVariant = "default" | "emerald" | "gold";

const TINTS: Record<MetalVariant, { glow: string | null }> = {
  default: { glow: null },
  emerald: { glow: "rgba(80, 220, 140, 0.55)" },
  gold: { glow: "rgba(255, 196, 80, 0.55)" },
};

interface LiquidMetalButtonProps {
  label?: string;
  onClick?: () => void;
  href?: string;
  viewMode?: "text" | "icon";
  width?: number;
  variant?: MetalVariant;
  /** face/label tone — 'auto' = white in light theme, black in dark theme */
  appearance?: MetalAppearance;
  /** custom icon for viewMode="icon" (inherits the tone via currentColor); defaults to Sparkles */
  icon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}

export function LiquidMetalButton({
  label = "Get Started",
  onClick,
  href,
  viewMode = "text",
  width: customWidth,
  variant = "default",
  appearance = "dark",
  icon,
  className,
  disabled = false,
  type = "button",
}: LiquidMetalButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [shaderLoaded, setShaderLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const tint = TINTS[variant] ?? TINTS.default;
  const [ripples, setRipples] = useState<
    Array<{ x: number; y: number; id: number }>
  >([]);
  const shaderRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/suspicious/noExplicitAny: External library without types
  const shaderMount = useRef<any>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleId = useRef(0);

  // Dynamic width calculation — avoids clipping strings like "In den Warenkorb"
  const dimensions = useMemo(() => {
    if (viewMode === "icon") {
      return {
        width: 46,
        height: 46,
        innerWidth: 42,
        innerHeight: 42,
        shaderWidth: 46,
        shaderHeight: 46,
      };
    } else {
      // Calculate sufficient width for text if not explicitly provided
      const estimatedWidth = Math.max(142, (label?.length || 0) * 9 + 48);
      const w = customWidth || estimatedWidth;
      return {
        width: w,
        height: 46,
        innerWidth: w - 4,
        innerHeight: 42,
        shaderWidth: w,
        shaderHeight: 46,
      };
    }
  }, [viewMode, customWidth, label]);

  // Honour prefers-reduced-motion, live. When set, we skip the continuously
  // animating WebGL shader entirely and show the static CSS metal face — which
  // also frees a WebGL context (the lava background already holds one).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const styleId = "shader-canvas-style-exploded";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .shader-container-exploded canvas {
          width: 100% !important;
          height: 100% !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          border-radius: 100px !important;
        }
        @keyframes ripple-animation {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    let isMounted = true;

    const loadShader = async () => {
      try {
        const { liquidMetalFragmentShader, ShaderMount } = await import(
          "@paper-design/shaders"
        );

        if (shaderRef.current && isMounted) {
          if (shaderMount.current?.destroy) {
            shaderMount.current.destroy();
          }

          shaderMount.current = new ShaderMount(
            shaderRef.current,
            liquidMetalFragmentShader,
            {
              u_repetition: 4,
              u_softness: 0.5,
              u_shiftRed: 0.3,
              u_shiftBlue: 0.3,
              u_distortion: 0,
              u_contour: 0,
              u_angle: 45,
              u_scale: 8,
              u_shape: 1,
              u_offsetX: 0.1,
              u_offsetY: -0.1,
            },
            undefined,
            0.6,
          );
          setShaderLoaded(true);
        }
      } catch (error) {
        // Fallback gracefully to CSS metallic border if WebGL contexts are exhausted or unavailable
        console.warn("[LiquidMetalButton] WebGL shader unavailable, using fallback:", error);
      }
    };

    if (prefersReducedMotion) {
      // Ensure the static CSS metal face is shown, not a transparent shader box.
      setShaderLoaded(false);
    } else {
      loadShader();
    }

    return () => {
      isMounted = false;
      if (shaderMount.current?.destroy) {
        shaderMount.current.destroy();
        shaderMount.current = null;
      }
    };
  }, [prefersReducedMotion]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    shaderMount.current?.setSpeed?.(1);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPressed(false);
    shaderMount.current?.setSpeed?.(0.6);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (shaderMount.current?.setSpeed) {
      shaderMount.current.setSpeed(2.4);
      setTimeout(() => {
        shaderMount.current?.setSpeed?.(isHovered ? 1 : 0.6);
      }, 500);
    }

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const newRipple = {
        x,
        y,
        id: rippleId.current++,
      };

      setRipples((prev) => [...prev, newRipple]);

      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
      }, 600);
    }

    onClick?.();
  };

  // Colored halo only on hover, only for tinted variants — keeps the resting
  // state a restrained dark slab.
  const glowRing = tint.glow && isHovered && !disabled ? `, 0 0 22px 0 ${tint.glow}` : "";

  return (
    <div
      className={cn(styles.root, className)}
      data-appearance={appearance}
      style={{
        position: "relative",
        display: "inline-block",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
      }}
    >
      <div
        style={{
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            perspective: "1000px",
          }}
        >
          {/* Inner dark face */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 30,
            }}
          >
            {viewMode === "icon" &&
              (icon ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--lmb-icon)",
                    transition: "transform 0.3s ease",
                    transform: isHovered ? "scale(1.12)" : "scale(1)",
                  }}
                >
                  {icon}
                </span>
              ) : (
                <Sparkles
                  style={{
                    width: "20px",
                    height: "20px",
                    color: "var(--lmb-icon)",
                    transition: "transform 0.3s ease",
                    transform: isHovered ? "rotate(12deg) scale(1.1)" : "rotate(0) scale(1)",
                  }}
                />
              ))}
            {viewMode === "text" && (
              <span
                style={{
                  fontSize: "14px",
                  color: "var(--lmb-label)",
                  fontWeight: 600,
                  textShadow: "var(--lmb-label-shadow)",
                  transition: "all 0.3s ease",
                  transform: "scale(1)",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            )}
          </div>

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              transformStyle: "preserve-3d",
              transition:
                "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease",
              transform: `translateZ(10px) ${isPressed ? "translateY(1px) scale(0.98)" : "translateY(0) scale(1)"}`,
              zIndex: 20,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: `${(dimensions.height - dimensions.innerHeight) / 2}px`,
                left: `${(dimensions.width - dimensions.innerWidth) / 2}px`,
                width: `${dimensions.innerWidth}px`,
                height: `${dimensions.innerHeight}px`,
                borderRadius: "100px",
                background: "var(--lmb-face)",
                boxShadow: isPressed
                  ? "inset 0px 2px 4px rgba(0, 0, 0, 0.4), inset 0px 1px 2px rgba(0, 0, 0, 0.3)"
                  : "none",
                transition:
                  "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease, box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>

          {/* WebGL metal shader or CSS metallic fallback rim */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              transformStyle: "preserve-3d",
              transition:
                "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease",
              transform: `translateZ(0px) ${isPressed ? "translateY(1px) scale(0.98)" : "translateY(0) scale(1)"}`,
              zIndex: 10,
            }}
          >
            <div
              style={{
                height: `${dimensions.height}px`,
                width: `${dimensions.width}px`,
                borderRadius: "100px",
                boxShadow:
                  (isPressed
                    ? "0px 0px 0px 1px rgba(0, 0, 0, 0.5), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)"
                    : isHovered
                      ? "0px 0px 0px 1px rgba(0, 0, 0, 0.4), 0px 12px 6px 0px rgba(0, 0, 0, 0.05), 0px 8px 5px 0px rgba(0, 0, 0, 0.1), 0px 4px 4px 0px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.2)"
                      : "0px 0px 0px 1px rgba(0, 0, 0, 0.3), 0px 36px 14px 0px rgba(0, 0, 0, 0.02), 0px 20px 12px 0px rgba(0, 0, 0, 0.08), 0px 9px 9px 0px rgba(0, 0, 0, 0.12), 0px 2px 5px 0px rgba(0, 0, 0, 0.15)") +
                  glowRing,
                transition:
                  "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease, box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                background: shaderLoaded
                  ? "rgb(0 0 0 / 0)"
                  : "linear-gradient(135deg, #a0a5aa 0%, #40454c 50%, #70757a 100%)", // CSS metallic sheen fallback
              }}
            >
              <div
                ref={shaderRef}
                className="shader-container-exploded"
                style={{
                  borderRadius: "100px",
                  overflow: "hidden",
                  position: "relative",
                  width: `${dimensions.shaderWidth}px`,
                  maxWidth: `${dimensions.shaderWidth}px`,
                  height: `${dimensions.shaderHeight}px`,
                  transition: "width 0.4s ease, height 0.4s ease",
                }}
              />
            </div>
          </div>

          {(() => {
            const isExternal = href ? /^(https?:|\/\/|mailto:|tel:)/i.test(href) : false;
            // Strip leading locale prefix (e.g. /de/slug -> /slug) because next-intl's localized Link
            // automatically prepends the current locale.
            const targetHref = href && !isExternal
              ? href.trim().replace(/^(\/(?:de|en))+(?=\/|$)/i, "") || "/"
              : href;

            const interactiveStyle: React.CSSProperties = {
              position: "absolute",
              top: 0,
              left: 0,
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              background: "transparent",
              border: "none",
              cursor: disabled ? "not-allowed" : "pointer",
              outline: "none",
              zIndex: 40,
              transformStyle: "preserve-3d",
              transform: "translateZ(25px)",
              transition:
                "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.4s ease, height 0.4s ease",
              overflow: "hidden",
              borderRadius: "100px",
              display: href ? "block" : undefined,
            };

            const rippleElements = ripples.map((ripple) => (
              <span
                key={ripple.id}
                style={{
                  position: "absolute",
                  left: `${ripple.x}px`,
                  top: `${ripple.y}px`,
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%)",
                  pointerEvents: "none",
                  animation: "ripple-animation 0.6s ease-out",
                }}
              />
            ));

            if (href) {
              if (isExternal) {
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClick as any}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseDown={() => !disabled && setIsPressed(true)}
                    onMouseUp={() => !disabled && setIsPressed(false)}
                    style={interactiveStyle}
                    aria-label={label}
                  >
                    {rippleElements}
                  </a>
                );
              }

              return (
                <Link
                  href={targetHref!}
                  onClick={handleClick as any}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onMouseDown={() => !disabled && setIsPressed(true)}
                  onMouseUp={() => !disabled && setIsPressed(false)}
                  style={interactiveStyle}
                  aria-label={label}
                >
                  {rippleElements}
                </Link>
              );
            }

            return (
              <button
                ref={buttonRef}
                type={type}
                disabled={disabled}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseDown={() => !disabled && setIsPressed(true)}
                onMouseUp={() => !disabled && setIsPressed(false)}
                style={interactiveStyle}
                aria-label={label}
              >
                {rippleElements}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
